import { DateTime } from 'luxon';

/**
 * Generates a localized date range label for dashboard controls and date pickers using ICU date
 * formatting patterns.
 *
 * Transforms Luxon DateTime objects to the specified timezone, normalizes them to midnight for
 * consistent date-only formatting, then uses react-intl's formatMessage with ICU skeleton
 * patterns to produce locale-appropriate date range labels based on the relationship between dates.
 *
 * The function uses different ICU date skeleton patterns depending on date relationships:
 * - Same day: `::dMMMy` (e.g., "15 Mar 2024")
 * - Same month: `::d` and `::dMMMy` (e.g., "15 – 20 Mar 2024")
 * - Same year: `::dMMM` and `::dMMMy` (e.g., "15 Mar – 20 Apr 2024")
 * - Different years: `::dMMMy` for both (e.g., "15 Mar 2023 – 20 Jan 2024")
 * @param {import('react-intl').IntlShape} intl - The react-intl internationalization object for
 *   formatting dates according to the user's locale
 * @param {DateTime} s - Start date as a Luxon DateTime object, will be
 *   transformed to the specified timezone and normalized to midnight
 * @param {DateTime} f - Finish date as a Luxon DateTime object, will be
 *   transformed to the specified timezone and normalized to midnight
 * @param {string} timezone - Timezone for date transformation. Use 'system' for local timezone
 *   or IANA timezone ID (e.g., 'Australia/Sydney', 'UTC', 'America/New_York')
 * @returns {string} Localized date range label using ICU formatting patterns, or empty string if
 *   required parameters are missing
 * @see {@link https://unicode-org.github.io/icu/userguide/format_parse/datetime/ ICU DateTime Formatting}
 * @see {@link https://formatjs.github.io/docs/intl-messageformat/#datetimenumber-skeleton FormatJS Date/Time Skeleton Patterns}
 */
const buildLocalizedDateRangeLabel = (intl, s, f, timezone) => {
  if (!intl || (!s && !f)) { return ''; }

  const timeZoneOption = timezone === 'system' ? undefined : timezone;

  // Handle single day selection for PortfolioDateRangePicker: when user selects only a start date,
  // display it as a single date (e.g., "26 Sep 2024"). This supports portfolio workflows where
  // users can incrementally build date ranges by first selecting a start date, then optionally
  // adding an end date. Unlike Property Dashboard date picker which requires complete ranges,
  // Portfolio date picker allows partial selections.
  if ((s && !f)) {
    const startAsJSDate = new Date(s?.setZone(timeZoneOption).toFormat("yyyy-LL-dd'T'00:00:00"));
    return intl.formatMessage(
      {
        id: 'util.time.date_range.same_day.label',
        defaultMessage: '{start, date, ::dMMMy}',
      },
      { start: startAsJSDate },
    );
  }

  // Handle single day selection for PortfolioDateRangePicker: when user selects only an end date,
  // display it as a single date (e.g., "28 Sep 2024"). This supports portfolio workflows where
  // users might want to view data up to a specific date without specifying a start date.
  if (!s && f) {
    const finishAsJSDate = new Date(f?.setZone(timeZoneOption).toFormat("yyyy-LL-dd'T'00:00:00"));
    return intl.formatMessage(
      {
        id: 'util.time.date_range.same_day.label',
        defaultMessage: '{finish, date, ::dMMMy}',
      },
      { finish: finishAsJSDate },
    );
  }

  const startAsJSDate = new Date(s?.setZone(timeZoneOption).toFormat("yyyy-LL-dd'T'00:00:00"));
  const finishAsJSDate = new Date(f?.setZone(timeZoneOption).toFormat("yyyy-LL-dd'T'00:00:00"));

  // single day
  if (s.hasSame(f, 'day')) {
    return intl.formatMessage(
      {
        id: 'util.time.date_range.same_day.label',
        defaultMessage: '{start, date, ::dMMMy}',
      },
      { start: startAsJSDate },
    );
  }

  // two dates in the same month
  if (s.hasSame(f, 'year') && s.hasSame(f, 'month')) {
    return intl.formatMessage(
      {
        id: 'util.time.date_range.same_month.label',
        defaultMessage: '{start, date, ::d} – {finish, date, ::dMMMy}',
      },
      { start: startAsJSDate, finish: finishAsJSDate },
    );
  }

  // two dates in the same year
  if (s.hasSame(f, 'year')) {
    return intl.formatMessage(
      {
        id: 'util.time.date_range.same_year.label',
        defaultMessage: '{start, date, ::dMMM} – {finish, date, ::dMMMy}',
      },
      { start: startAsJSDate, finish: finishAsJSDate },
    );
  }

  // two dates in different years
  return intl.formatMessage(
    {
      id: 'util.time.date_range.different_years.label',
      defaultMessage: '{start, date, ::dMMMy} – {finish, date, ::dMMMy}',
    },
    { start: startAsJSDate, finish: finishAsJSDate },
  );
};

export default buildLocalizedDateRangeLabel;
